import { fabric } from "fabric";

const REF_GRADIENT_W = 280;

/** Normalize any CSS color to #rrggbb for native color inputs. */
export function clockStyleColorForInput(val, fallback = "#ffffff") {
  return toHexColor(val, fallback);
}

/** @param {string|undefined} hex */
function toHexColor(hex, fallback = "#ffffff") {
  if (!hex || typeof hex !== "string") return fallback;
  const s = hex.trim();
  if (s.startsWith("#") && (s.length === 4 || s.length === 7)) return s;
  const m = s.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) {
    const r = (+m[1]).toString(16).padStart(2, "0");
    const g = (+m[2]).toString(16).padStart(2, "0");
    const b = (+m[3]).toString(16).padStart(2, "0");
    return `#${r}${g}${b}`;
  }
  return fallback;
}

/**
 * Default style keys per variant (used to merge saved partial styles).
 * @param {string} variant
 */
export function getClockStyleDefaults(variant) {
  if (variant === "longform-analog") {
    return {
      faceGradientA: "#39454b",
      faceGradientB: "#101017",
      faceStroke: "#39454b",
      /** RGBA string applied to Fabric shadow on the face circle */
      faceShadow: "rgba(145, 158, 165, 0.9)",
      handColor: "#b7ddf0",
      hourTextColor: "#ffffff",
      minuteTextColor: "#ffffff",
      calendarTextColor: "#ffffff",
      dialFill: "#4c5c64",
      dialStroke: "#101017",
    };
  }
  if (variant === "neumorphic-analog") {
    return {
      frameBg: "#e0e5ec",
      faceOuter: "#e0e5ec",
      faceInner: "#e0e5ec",
      faceInnerStroke: "#eef2f7",
      tickFill: "#e0e5ec",
      tickStroke: "#c5cedd",
      digitColor: "#b8c1d1",
      digitFontSize: 24,
      handHour: "#4a5463",
      handMinute: "#7a8a9e",
      handSecond: "#e65e5e",
      pinOuter: "#e0e5ec",
      pinInner: "#4a5463",
    };
  }
  if (variant === "liquid-crystal") {
    return {
      lcdBg: "#222222",
      lcdText: "#eeeeee",
    };
  }
  // gradient-night (hex only for <input type="color"> compatibility)
  return {
    cardMode: "gradient",
    cardSolid: "#283348",
    gradientColorLeft: "#141e30",
    gradientColorRight: "#243b55",
    timeColor: "#ffffff",
    subColor: "#ffffff",
    dateColor: "#ffffff",
    moonColor: "#ffffff",
  };
}

/**
 * @param {{ variant?: string, style?: Record<string, unknown> } | null | undefined} clockObj
 */
export function mergeClockObjStyle(clockObj) {
  const variant = clockObj?.variant || "gradient-night";
  const base = getClockStyleDefaults(variant);
  const s = clockObj?.style && typeof clockObj.style === "object" ? clockObj.style : {};
  return { ...base, ...s };
}

/**
 * Apply merged style to clock-widget group children. Safe to call repeatedly (e.g. after time refresh).
 * @param {import('fabric').fabric.Group} group
 * @param {{ variant?: string, style?: Record<string, unknown> }} clockObj
 */
export function applyClockWidgetStyle(group, clockObj) {
  if (!group || group.type !== "group" || group.eleType !== "clock-widget") return;
  applyClockColors(group, clockObj);
  applyClockShadows(group, clockObj);
}

/**
 * Default drop shadows per clock part, matching what EditorOps builds. Kept here so
 * the shadow can be switched off and back on again after a save/reload.
 */
const CLOCK_PART_SHADOWS = {
  "clock-lcd-bg": { color: "rgba(0,0,0,0.85)", blur: 7, offsetX: 0, offsetY: 0 },
  "clock-lcd-text": { color: "rgba(255,255,255,0.4)", blur: 3, offsetX: 0, offsetY: 0 },
  "clock-gradient-card": { color: "rgba(0,0,0,0.45)", blur: 22, offsetX: 4, offsetY: 8 },
  "clock-analog-face-outer": { color: "rgba(163,177,198,0.55)", blur: 22, offsetX: 10, offsetY: 10 },
  "clock-analog-face-inner": { color: "rgba(255,255,255,0.45)", blur: 6, offsetX: -4, offsetY: -4 },
  "clock-analog-digit": { color: "rgba(255,255,255,0.7)", blur: 0, offsetX: 1, offsetY: 1 },
  "clock-hand-hour": { color: "rgba(0,0,0,0.12)", blur: 4, offsetX: 2, offsetY: 2 },
  "clock-hand-minute": { color: "rgba(0,0,0,0.12)", blur: 4, offsetX: 2, offsetY: 2 },
  "clock-hand-second": { color: "rgba(0,0,0,0.12)", blur: 4, offsetX: 2, offsetY: 2 },
  "clock-analog-pin-outer": { color: "rgba(163,177,198,0.5)", blur: 5, offsetX: 2, offsetY: 2 },
  "clock-longform-face": { color: "rgba(145, 158, 165, 0.9)", blur: 5, offsetX: 15, offsetY: 15 },
};

/** `style.shadow === false` removes every drop shadow in the widget; otherwise defaults apply. */
function applyClockShadows(group, clockObj) {
  const style = mergeClockObjStyle(clockObj);
  const on = style.shadow !== false;
  const subs = group._objects || group.getObjects?.() || [];
  subs.forEach((c) => {
    const def = CLOCK_PART_SHADOWS[c.name];
    if (!def) return;
    if (!on) {
      c.set({ shadow: null });
      return;
    }
    const color =
      c.name === "clock-longform-face" && typeof style.faceShadow === "string" && style.faceShadow
        ? style.faceShadow
        : def.color;
    c.set({ shadow: new fabric.Shadow({ ...def, color }) });
  });
  group.dirty = true;
}

function applyClockColors(group, clockObj) {
  const variant = clockObj?.variant || "gradient-night";
  const style = mergeClockObjStyle(clockObj);
  const subs = group._objects || group.getObjects?.() || [];

  if (variant === "liquid-crystal") {
    const bg = subs.find((c) => c.name === "clock-lcd-bg") || subs.find((c) => c.type === "rect");
    const txt = subs.find((c) => c.name === "clock-lcd-text");
    if (bg) bg.set({ fill: toHexColor(style.lcdBg, "#222222") });
    if (txt) {
      txt.set({ fill: toHexColor(style.lcdText, "#eeeeee") });
      if (typeof txt.initDimensions === "function") txt.initDimensions();
    }
    return;
  }

  if (variant === "longform-analog") {
    const face = subs.find((c) => c.name === "clock-longform-face");
    if (face) {
      face.set({
        fill: new fabric.Gradient({
          type: "linear",
          gradientUnits: "pixels",
          coords: { x1: 0, y1: 300, x2: 300, y2: 0 },
          colorStops: [
            { offset: 0, color: toHexColor(style.faceGradientA, "#39454b") },
            { offset: 0.22, color: toHexColor(style.faceGradientA, "#39454b") },
            { offset: 1, color: toHexColor(style.faceGradientB, "#101017") },
          ],
        }),
        stroke: toHexColor(style.faceStroke, "#39454b"),
      });
    }
    const hc = toHexColor(style.handColor, "#b7ddf0");
    const hh = subs.find((c) => c.name === "clock-longform-hour-hand");
    const hm = subs.find((c) => c.name === "clock-longform-minute-hand");
    if (hh) hh.set({ stroke: hc });
    if (hm) hm.set({ stroke: hc });
    const secG = subs.find((c) => c.name === "clock-longform-second-hand");
    if (secG && secG.type === "group") {
      const subObjs = secG.getObjects?.() || secG._objects || [];
      subObjs.forEach((ch) => {
        const t = (ch.type || "").toLowerCase();
        if (t === "line" || t === "ellipse") ch.set({ stroke: hc });
      });
    }
    const ht = subs.find((c) => c.name === "clock-longform-hour-text");
    const mt = subs.find((c) => c.name === "clock-longform-minute-text");
    const ct = subs.find((c) => c.name === "clock-longform-calendar-text");
    if (ht) {
      ht.set({ fill: toHexColor(style.hourTextColor, "#ffffff") });
      if (typeof ht.initDimensions === "function") ht.initDimensions();
    }
    if (mt) {
      mt.set({ fill: toHexColor(style.minuteTextColor, "#ffffff") });
      if (typeof mt.initDimensions === "function") mt.initDimensions();
    }
    if (ct) {
      ct.set({ fill: toHexColor(style.calendarTextColor, "#ffffff") });
      if (typeof ct.initDimensions === "function") ct.initDimensions();
    }
    const dial = subs.find((c) => c.name === "clock-longform-dial");
    if (dial) {
      dial.set({
        fill: toHexColor(style.dialFill, "#4c5c64"),
        stroke: toHexColor(style.dialStroke, "#101017"),
      });
    }
    return;
  }

  if (variant === "neumorphic-analog") {
    const bg = subs.find((c) => c.name === "clock-analog-bg");
    const faceOuter = subs.find((c) => c.name === "clock-analog-face-outer");
    const faceInner = subs.find((c) => c.name === "clock-analog-face-inner");
    if (bg) bg.set({ fill: toHexColor(style.frameBg, "#e0e5ec") });
    if (faceOuter) faceOuter.set({ fill: toHexColor(style.faceOuter, "#e0e5ec") });
    if (faceInner) {
      faceInner.set({
        fill: toHexColor(style.faceInner, "#e0e5ec"),
        stroke: toHexColor(style.faceInnerStroke, "#eef2f7"),
      });
    }
    subs
      .filter((c) => c.name === "clock-analog-tick")
      .forEach((t) => {
        t.set({
          fill: toHexColor(style.tickFill, "#e0e5ec"),
          stroke: toHexColor(style.tickStroke, "#c5cedd"),
        });
      });
    const digitSize = Number(style.digitFontSize);
    const dSize = Number.isFinite(digitSize) ? Math.min(40, Math.max(10, Math.round(digitSize))) : 24;
    subs
      .filter((c) => c.name === "clock-analog-digit")
      .forEach((t) => {
        t.set({
          fill: toHexColor(style.digitColor, "#b8c1d1"),
          fontSize: dSize,
        });
        if (typeof t.initDimensions === "function") t.initDimensions();
      });
    const h = subs.find((c) => c.name === "clock-hand-hour");
    const m = subs.find((c) => c.name === "clock-hand-minute");
    const sLine = subs.find((c) => c.name === "clock-hand-second");
    if (h) h.set({ stroke: toHexColor(style.handHour, "#4a5463") });
    if (m) m.set({ stroke: toHexColor(style.handMinute, "#7a8a9e") });
    if (sLine) sLine.set({ stroke: toHexColor(style.handSecond, "#e65e5e") });
    const pinO = subs.find((c) => c.name === "clock-analog-pin-outer");
    const pinI = subs.find((c) => c.name === "clock-analog-pin-inner");
    if (pinO) pinO.set({ fill: toHexColor(style.pinOuter, "#e0e5ec") });
    if (pinI) pinI.set({ fill: toHexColor(style.pinInner, "#4a5463") });
    return;
  }

  // gradient-night
  const card =
    subs.find((c) => c.name === "clock-gradient-card") ||
    subs.find((c) => c.type === "rect" && (c.width || 0) >= 200);
  const timeMain = subs.find((c) => c.name === "clock-time-main");
  const timeSub = subs.find((c) => c.name === "clock-time-sub");
  const dateText = subs.find((c) => c.name === "clock-date");
  const moon = subs.find((c) => c.name === "clock-moon");

  if (card) {
    if (style.cardMode === "solid") {
      card.set({ fill: toHexColor(style.cardSolid, "#283348") });
    } else {
      card.set({
        fill: new fabric.Gradient({
          type: "linear",
          gradientUnits: "pixels",
          coords: { x1: 0, y1: 0, x2: REF_GRADIENT_W, y2: 0 },
          colorStops: [
            { offset: 0, color: toHexColor(style.gradientColorLeft, "#141e30") },
            { offset: 1, color: toHexColor(style.gradientColorRight, "#243b55") },
          ],
        }),
      });
    }
  }
  if (timeMain) {
    timeMain.set({ fill: toHexColor(style.timeColor, "#ffffff") });
    if (typeof timeMain.initDimensions === "function") timeMain.initDimensions();
  }
  if (timeSub) {
    timeSub.set({ fill: toHexColor(style.subColor, "#ffffff") });
    if (typeof timeSub.initDimensions === "function") timeSub.initDimensions();
  }
  if (dateText) {
    dateText.set({ fill: toHexColor(style.dateColor, "#ffffff") });
    if (typeof dateText.initDimensions === "function") dateText.initDimensions();
  }
  if (moon) moon.set({ fill: toHexColor(style.moonColor, "#ffffff") });
}
